import React, { useEffect, useRef, useCallback } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import {
  renderText,
  useChatContext,
  useChannelStateContext,
  type MessageContextValue,
} from 'stream-chat-react'
import RichTextMessagingInput from '../RichTextInput/RichTextMessagingInput'
import type { DefaultStreamChatGenerics } from '../../types'
import type { Attachment } from 'stream-chat'

/* ============================================================================
   Types
============================================================================ */
interface EditMessageFormProps<
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> {
  show: boolean
  message: MessageContextValue<StreamChatGenerics>['message']
  clearEditingState: () => void
  isInThread?: boolean
}

/* ============================================================================
   Constants
============================================================================ */
const WRAP_ID = 'edit-message-form'

// Removed legacy toolbar classes after migrating to RichTextMessagingInput

/* ============================================================================
   Helper: set value on a controlled textarea the "React-safe" way
   (use the native property setter and dispatch an input event so React state updates)
============================================================================ */
// Legacy helper kept for reference; no longer used after migration

/* ============================================================================
   Robust HTML → Markdown Conversion (Stream renders markdown, not HTML)
   Handles nested formatting recursively to preserve all formatting combinations
============================================================================ */

/**
 * Recursively converts a DOM node to markdown, preserving nested formatting
 */
function nodeToMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || ''
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return ''
  }

  const el = node as Element
  const tagName = el.tagName.toLowerCase()

  // Process children recursively first
  let content = ''
  for (const child of Array.from(el.childNodes)) {
    content += nodeToMarkdown(child)
  }

  // Handle block elements
  if (['p', 'div', 'section', 'article', 'header', 'footer'].includes(tagName)) {
    return content ? `${content}\n` : ''
  }

  if (tagName === 'br') {
    return '\n'
  }

  // Handle formatting elements - these wrap their content
  if (tagName === 'strong' || tagName === 'b') {
    return `**${content}**`
  }

  if (tagName === 'em' || tagName === 'i') {
    return `*${content}*`
  }

  if (tagName === 's' || tagName === 'strike' || tagName === 'del') {
    return `~~${content}~~`
  }

  // Handle spans with inline styles
  // Support multiple styles (e.g., bold + strikethrough)
  if (tagName === 'span') {
    const style = el.getAttribute('style')?.toLowerCase() || ''
    let formattedContent = content

    // Apply formatting in order: bold, italic, strikethrough
    // This allows multiple formats to be combined
    if (/font-weight\s*:\s*(bold|600|700|800|900)/.test(style)) {
      formattedContent = `**${formattedContent}**`
    }
    if (/font-style\s*:\s*italic/.test(style)) {
      formattedContent = `*${formattedContent}*`
    }
    if (/text-decoration\s*:\s*line-through/.test(style)) {
      formattedContent = `~~${formattedContent}~~`
    }

    return formattedContent
  }

  // Handle links
  if (tagName === 'a') {
    const href = el.getAttribute('href') ?? ''
    const text = content.trim() || href
    return `[${text}](${href})`
  }

  // Handle lists
  if (tagName === 'ul') {
    const items = Array.from(el.querySelectorAll(':scope > li'))
    const md = items
      .map((li) => {
        let itemContent = ''
        for (const child of Array.from(li.childNodes)) {
          itemContent += nodeToMarkdown(child)
        }
        return `- ${itemContent.trim()}`
      })
      .join('\n')
    return `${md}\n`
  }

  if (tagName === 'ol') {
    const items = Array.from(el.querySelectorAll(':scope > li'))
    const md = items
      .map((li, i) => {
        let itemContent = ''
        for (const child of Array.from(li.childNodes)) {
          itemContent += nodeToMarkdown(child)
        }
        return `${i + 1}. ${itemContent.trim()}`
      })
      .join('\n')
    return `${md}\n`
  }

  // For other elements, just return the content
  return content
}

function htmlToMarkdown(html: string): string {
  if (!html || !html.trim()) {
    return ''
  }

  const root = document.createElement('div')
  root.innerHTML = html

  // Process all child nodes recursively
  let markdown = ''
  for (const child of Array.from(root.childNodes)) {
    markdown += nodeToMarkdown(child)
  }

  // Cleanup
  return markdown
    .replace(/\u00a0/g, ' ') // nbsp -> space
    .replace(/[ \t]+\n/g, '\n') // strip trailing spaces
    .replace(/\n{3,}/g, '\n\n') // collapse >2 newlines
    .trim()
}

/* ============================================================================
   Toolbar Actions (contenteditable) – using execCommand for broad support
============================================================================ */
// Legacy toolbar helpers removed

// Legacy toolbar helpers removed

/* ============================================================================
   Component
============================================================================ */
export const EditMessageForm = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>({
  show,
  message,
  clearEditingState,
  isInThread = false,
}: EditMessageFormProps<StreamChatGenerics>) => {
  /* ------------------------------ Refs & State ---------------------------- */
  const editorRef = useRef<HTMLDivElement | null>(null)
  const { client } = useChatContext<StreamChatGenerics>()
  const { channel } = useChannelStateContext<StreamChatGenerics>()

  // pressed state for inline formats based on selection
  // Legacy formatting state removed

  /* --------------------------- Selection tracking ------------------------- */
  const refreshActiveFromSelection = useCallback(() => {}, [])

  useEffect(() => {
    // Global edit lock to coordinate with main composer
    if (show) {
      ;(window as unknown as { __CHAT_EDITING__?: boolean }).__CHAT_EDITING__ = true
    }
    if (!show) return
    refreshActiveFromSelection()
    document.addEventListener('selectionchange', refreshActiveFromSelection)
    return () => {
      ;(window as unknown as { __CHAT_EDITING__?: boolean }).__CHAT_EDITING__ = false
      document.removeEventListener('selectionchange', refreshActiveFromSelection)
    }
  }, [show, refreshActiveFromSelection])

  /* ---------------------- Derive initial HTML content ---------------------- */
  const getInitialContentHtml = () => {
    if (!message) return ''

    // 1) Prefer rich-text HTML we stored when sending
    const customHtml = (message as any)?.custom?.richTextHtml
    if (typeof customHtml === 'string' && customHtml.trim().length > 0) {
      return customHtml
    }

    // 2) Fall back to Stream's stored HTML if present
    const messageHtml = (message as any).html
    if (typeof messageHtml === 'string' && messageHtml.trim().length > 0) {
      return messageHtml
    }

    // 3) Convert markdown to HTML manually for proper editor display
    const text = message.text ?? ''
    if (text) {
      // Convert markdown formatting to HTML
      // Order matters: process bold before italic to avoid conflicts with single asterisks
      let html = text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') // bold
        .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>') // italic (single asterisks not part of bold)
        .replace(/~~(.+?)~~/g, '<s>$1</s>') // strikethrough
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>') // links
        .replace(/\n/g, '<br>') // newlines
      return `<p>${html}</p>`
    }

    // 4) Fallback: Render markdown/text using Stream's renderer
    const rendered = renderText(message.text ?? '')

    if (typeof rendered === 'string') {
      return rendered
    }
    if (React.isValidElement(rendered)) {
      return renderToStaticMarkup(rendered)
    }
    return String(rendered ?? message.text ?? '')
  }

  /* ---------------------- Initialize editor content ----------------------- */
  useEffect(() => {
    if (!show || !editorRef.current) return
    editorRef.current.innerHTML = getInitialContentHtml()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, message?.id])

  /* ------------------------------ DOM Helpers ----------------------------- */
  // Legacy DOM helper removed

  /* ------------------------------ Toolbar Model --------------------------- */
  // Legacy Tool type removed

  // Legacy inline toggle removed

  // Legacy emoji trigger removed

  // Legacy attach trigger removed

  // Removed legacy toolbar after migrating to RichTextMessagingInput

  /* -------------------------------- Handlers ------------------------------ */
  // Removed legacy save handler; using direct update through RichTextMessagingInput

  const handleSaveWithDirectUpdate = useCallback(
    async (richTextContent?: string, options?: { attachments?: any[] }) => {
      try {
        // Prefer richTextContent provided by RichTextMessagingInput, fallback to DOM
        const html =
          (typeof richTextContent === 'string' && richTextContent.length > 0
            ? richTextContent
            : editorRef.current?.innerHTML) ?? ''
        const md = htmlToMarkdown(html)
        if (!client || !message?.id || !channel?.cid) {
          console.error('Missing required data for message update')
          return
        }

        // CRITICAL: Update the message directly by ID and CID
        // This is the ONLY path that should fire for edits from this form
        const fallbackAttachments =
          (message.attachments as Attachment<DefaultStreamChatGenerics>[]) || []

        const nextAttachments =
          (options?.attachments as Attachment<DefaultStreamChatGenerics>[] | undefined) ||
          fallbackAttachments

        await client.updateMessage({
          id: message.id,
          text: md,
          attachments: nextAttachments,
          pinned: !!(message as any).pinned,
          custom: {
            ...((message as any).custom || {}),
            richText: true,
            richTextHtml: html,
          },
        } as any)

        // Immediately clear all editing state and locks
        clearEditingState()
        ;(window as unknown as { __CHAT_EDITING__?: boolean }).__CHAT_EDITING__ = false
      } catch (e) {
        console.error('Failed to update message:', e)
        // Clear locks even on error
        ;(window as unknown as { __CHAT_EDITING__?: boolean }).__CHAT_EDITING__ = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, message.id, message.attachments, channel?.cid, clearEditingState]
  )

  const triggerSaveSubmit = useCallback(() => {
    const form = document.getElementById(WRAP_ID)?.querySelector('form')
    if (form instanceof HTMLFormElement) {
      form.requestSubmit()
    }
  }, [])

  /* --------------------------- Conditional Render ------------------------- */
  if (!show) return null

  /* --------------------------------- Render ------------------------------- */
  return (
    <div id={WRAP_ID} className="mt-2 w-full min-w-0">
      <div className="flex gap-3 justify-end min-w-0">
        <div
          className={`flex flex-col items-end min-w-0 ${
            isInThread ? 'w-full max-w-full' : 'max-w-[75%]'
          }`}
        >
          <div className="w-full min-w-0 max-w-full bg-white rounded-2xl border border-app-grey-25 shadow-sm overflow-hidden">
            {/* Header – light grey background for separation */}
            <div className="flex items-center justify-between px-4 py-3 bg-app-grey-7 rounded-t-2xl">
              <div className="flex items-center gap-2">
                <svg
                  className="w-3.5 h-3.5 text-app-grey-55"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
                <span className="text-xs font-medium text-app-grey-55">Editing message</span>
              </div>
              <button
                type="button"
                onClick={clearEditingState}
                className="p-1 rounded-lg hover:bg-app-grey-15 transition-colors text-app-grey-55"
                aria-label="Close editing"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Input */}
            <div className="px-3 pb-2 min-w-0 overflow-hidden">
              <RichTextMessagingInput
                initialContent={getInitialContentHtml()}
                initialAttachments={message?.attachments as Attachment<DefaultStreamChatGenerics>[]}
                onSubmitOverride={handleSaveWithDirectUpdate}
                onCancel={clearEditingState}
                groupFormatting={isInThread}
              />
            </div>

            {/* Cancel / Save – own row, no extra border */}
            <div className="flex items-center justify-end gap-2 px-3 pb-3">
              <button
                type="button"
                onClick={clearEditingState}
                className="rounded-lg border border-app-grey-25 bg-white px-4 py-1.5 text-sm font-medium text-app-grey-80 transition-colors hover:bg-app-grey-5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={triggerSaveSubmit}
                className="rounded-lg bg-app-primary-70 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-app-primary-80"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hide Stream's default send/submit inside edit form so we use our footer buttons */}
      <style>{`
        #${WRAP_ID} [data-testid="emoji-picker-button"],
        #${WRAP_ID} [data-testid="attach-button"],
        #${WRAP_ID} .str-chat__message-textarea-emoji-picker-button,
        #${WRAP_ID} .str-chat__message-textarea-attach-button,
        #${WRAP_ID} .str-chat__send-button,
        #${WRAP_ID} button[type="submit"] {
          display: none !important;
        }
      `}</style>
    </div>
  )
}
